/**
 * MesAccompagnementsPage — item 9.3, côté demandeur.
 *
 * ─── LE PARCOURS, EN TROIS TEMPS ────────────────────────────────────────────
 *
 * On voit ce que la mairie propose, on demande, on dépose les pièces, on soumet.
 * Une fois soumise, la demande n'est plus modifiable : ce que la mairie lit ne
 * doit pas bouger sous ses yeux. Pour corriger, elle refuse avec un motif et le
 * demandeur repart d'une demande neuve — circuit court assumé pour cette
 * première tranche.
 *
 * ─── POURQUOI LES PIÈCES NE BLOQUENT PAS LE DÉPÔT ───────────────────────────
 *
 * Les pièces obligatoires sont vérifiées à la SOUMISSION. Une association doit
 * pouvoir déposer ce qu'elle a le lundi et le reste le jeudi ; exiger tout d'un
 * coup ferait abandonner au premier fichier manquant.
 */
import { useCallback, useEffect, useState } from 'react';
import { HandHeart, Upload, Send, Paperclip, CheckCircle2, AlertCircle, CalendarDays } from 'lucide-react';
import { api, ApiException, type ServiceMairieDTO, type DemandeServiceDTO } from '@/lib/api';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Alert, Badge, SectionEyebrow } from '@/components/ui';

const STATUT_LABEL: Record<string, string> = {
  brouillon: 'À compléter',
  soumise: 'Envoyée à la mairie',
  acceptee: 'Acceptée',
  refusee: 'Refusée',
  cloturee: 'Clôturée',
};

export function MesAccompagnementsPage() {
  const [services, setServices] = useState<ServiceMairieDTO[] | null>(null);
  const [demandes, setDemandes] = useState<DemandeServiceDTO[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [s, d] = await Promise.all([
        api.associations.services.catalogue(),
        api.associations.services.mesDemandes(),
      ]);
      setServices(s.services);
      setDemandes(d.demandes);
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function demander(s: ServiceMairieDTO) {
    setBusy(s.id); setErreur(null); setInfo(null);
    try {
      await api.associations.services.demander({ serviceId: s.id });
      setInfo(`Demande créée. Déposez les pièces, puis envoyez-la à la mairie.`);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Demande impossible');
    } finally { setBusy(null); }
  }

  async function deposer(d: DemandeServiceDTO, pieceId: string, file: File) {
    setBusy(d.id); setErreur(null);
    try {
      await api.associations.services.deposerPiece(d.id, file, pieceId);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Dépôt impossible');
    } finally { setBusy(null); }
  }

  async function soumettre(d: DemandeServiceDTO) {
    setBusy(d.id); setErreur(null); setInfo(null);
    try {
      await api.associations.services.soumettre(d.id);
      setInfo('Demande envoyée à la mairie.');
      await charger();
    } catch (e) {
      // Le serveur ÉNUMÈRE les pièces manquantes : on affiche son message tel
      // quel plutôt qu'un « dossier incomplet » qui obligerait à deviner.
      setErreur(e instanceof ApiException ? e.payload.error : 'Envoi impossible');
    } finally { setBusy(null); }
  }

  /** Les pièces attendues pour cette demande, d'après le service dont elle relève. */
  function piecesAttendues(d: DemandeServiceDTO) {
    return services?.find((s) => s.id === d.serviceId)?.pieces ?? [];
  }

  const dejaDemandes = new Set((demandes ?? [])
    .filter((d) => d.statut === 'brouillon' || d.statut === 'soumise')
    .map((d) => d.serviceId));

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Association / Acteurs locaux" accent="asso" />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <SectionEyebrow>Accompagnement</SectionEyebrow>
        <h1 className="mt-1 font-display text-xl font-semibold text-ink sm:text-2xl">
          Ce que la mairie propose
        </h1>

        {erreur && <Alert variant="error" icon={AlertCircle} className="mt-4">{erreur}</Alert>}
        {info && <Alert variant="success" icon={CheckCircle2} className="mt-4">{info}</Alert>}

        {services?.length === 0 && (
          <Card className="mt-4 p-5 text-sm text-ink-subtle">
            La mairie ne propose aucun accompagnement pour le moment.
          </Card>
        )}

        <div className="mt-4 space-y-3">
          {services?.map((s) => (
            <Card key={s.id} className="p-4">
              <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                {s.typeOffre === 'evenement'
                  ? <CalendarDays className="h-4 w-4" aria-hidden="true" />
                  : <HandHeart className="h-4 w-4" aria-hidden="true" />}
                {s.libelle}
                {s.placesMax != null && (
                  <Badge size="sm"
                    variant={s.placesPrises >= s.placesMax ? 'urgent' : 'neutral'}>
                    {s.placesPrises >= s.placesMax
                      ? 'Complet'
                      : `${s.placesMax - s.placesPrises} place${s.placesMax - s.placesPrises > 1 ? 's' : ''}`}
                  </Badge>
                )}
              </p>
              {s.typeOffre === 'evenement' && (
                <p className="mt-0.5 text-xs text-ink-muted">
                  {s.dateDebut ? s.dateDebut.split('-').reverse().join('/') : ''}
                  {s.lieu ? ` · ${s.lieu}` : ''}
                  {s.organisateurNom ? ` · organisé par ${s.organisateurNom}` : ''}
                  {s.inscriptionsJusquAu
                    ? ` · inscriptions jusqu’au ${s.inscriptionsJusquAu.split('-').reverse().join('/')}`
                    : ''}
                </p>
              )}
              {s.description && <p className="mt-1 text-sm text-ink-muted">{s.description}</p>}
              {s.pieces.length > 0 && (
                <p className="mt-1 text-xs text-ink-subtle">
                  Pièces à fournir : {s.pieces.map((p) => p.libelle).join(', ')}.
                </p>
              )}
              <div className="mt-3">
                {dejaDemandes.has(s.id) ? (
                  <p className="text-xs text-ink-subtle">
                    {s.typeOffre === 'evenement'
                      ? 'Vous êtes déjà inscrit à cet événement.'
                      : 'Vous avez déjà une demande en cours pour cet accompagnement.'}
                  </p>
                ) : s.placesMax != null && s.placesPrises >= s.placesMax ? (
                  // On affiche l'événement complet plutôt que de le masquer :
                  // savoir qu'il existe et qu'il est plein vaut mieux que de le
                  // chercher en vain.
                  <p className="text-xs text-ink-subtle">
                    Cet événement est complet.
                  </p>
                ) : (
                  <Button size="sm" disabled={busy === s.id} onClick={() => void demander(s)}>
                    {s.typeOffre === 'evenement' ? 'M’inscrire' : 'Demander cet accompagnement'}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {(demandes?.length ?? 0) > 0 && (
          <>
            <h2 className="mt-8 font-display text-lg font-semibold text-ink">Mes demandes</h2>
            <div className="mt-3 space-y-3">
              {demandes?.map((d) => {
                const attendues = piecesAttendues(d);
                const deposees = new Set(d.pieces.map((p) => p.pieceId));
                return (
                  <Card key={d.id} className="p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="text-ink">{d.serviceLibelle}</strong>
                      <Badge size="sm"
                        variant={d.statut === 'refusee' ? 'urgent'
                          : d.statut === 'acceptee' ? 'success' : 'neutral'}>
                        {STATUT_LABEL[d.statut] ?? d.statut}
                      </Badge>
                    </div>

                    {d.motifRefus && (
                      <p className="mt-2 rounded-r-lg bg-urgent-tint/50 p-2 text-xs text-urgent">
                        Motif du refus : {d.motifRefus}
                      </p>
                    )}
                    {d.reponse && (
                      <p className="mt-2 text-sm text-ink">Réponse de la mairie : {d.reponse}</p>
                    )}

                    {d.statut === 'brouillon' && (
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        {attendues.map((p) => (
                          <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-ink">{p.libelle}</span>
                            {!p.obligatoire && (
                              <span className="text-xs text-ink-muted">— facultative</span>
                            )}
                            {deposees.has(p.id) ? (
                              <span className="flex items-center gap-1 text-xs text-success-fg">
                                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> déposée
                              </span>
                            ) : (
                              <label className="ml-auto cursor-pointer text-xs text-tech-fg hover:underline">
                                <Upload className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                                Déposer
                                <input type="file" className="hidden"
                                  accept="application/pdf,image/*"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) void deposer(d, p.id, f);
                                    e.target.value = '';
                                  }} />
                              </label>
                            )}
                          </div>
                        ))}

                        <Button size="sm" variant="primary" disabled={busy === d.id}
                          onClick={() => void soumettre(d)}>
                          <Send className="h-4 w-4" aria-hidden="true" /> Envoyer à la mairie
                        </Button>
                      </div>
                    )}

                    {d.statut !== 'brouillon' && d.pieces.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {d.pieces.map((p) => (
                          <li key={p.id} className="flex items-center gap-1.5 text-xs text-ink-muted">
                            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                            {p.nomFichier}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
